import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { styled } from 'styled-components';

import { DatePicker, Popover, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import WizrFlexLayout from '../../../../../components/WizrFlexLayout';
import WizrUserJourney from '../../../../../components/WizrUserJourney';
import WizrButton from '../../../../../components/WizrButton';
import WizrText from '../../../../../components/WizrText';
import WizrTag from '../../../../../components/WizrTags';
import WizrTable from '../../../../../components/WizrTable';
import { useAutosolveTestHistorySelector, useBotDetailsSelector, useContentLoadingSelector } from '../../../../../redux/selectors/content';
import { dispatchAutosolveTestHistory, dispatchBotDetails } from '../../../../../redux/actions/content';
import WizrLoader from '../../../../../components/WizrLoader';
import DownloadIcon from '../../../../../assets/icons/DownloadIcon';

const uniqueTagList = ['1 day', '1 week', '1 month', '3 month', 'Custom'];

const StyledButtonWrapper = styled.div`
  border-radius: 50px;
  background: var(--grayscale-200, #eaecf1);
  width: 40px;
`;

type SelectDateActionPopOverProps = {
  onChange: (dates: any) => void;
};

const SelectDateActionPopOver: React.FC<SelectDateActionPopOverProps> = ({ onChange }) => (
  <WizrFlexLayout textAlign='left' background='#FFF'>
    <DatePicker.RangePicker onChange={onChange} />
  </WizrFlexLayout>
);

const AutoSolveTestHistory = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [duration, setDuration] = useState('1 month');

  // Redux selectors
  const botDetails = useBotDetailsSelector();
  const autosolveTestHistory = useAutosolveTestHistorySelector();
  const listInnerRef = useRef<HTMLDivElement | null>(null)
  const loading = useContentLoadingSelector()
  const [isLoading, setIsLoading] = useState(false);
  // Date filter logic (customize as needed)
  const getDateRangeFilter = (name: any) => {
    setDuration(name);
    let range;

    switch (name) {
      case '1 day':
        range = getDayRange();
        setShowDatePicker(false);
        getDateFilter(range.start, range.end);
        break;
      case '1 week':
        range = getWeekRange();
        setShowDatePicker(false);
        getDateFilter(range.start, range.end);
        break;
      case '1 month':
        range = getMonthRange();
        setShowDatePicker(false);
        getDateFilter(range.start, range.end);
        break;
      case '3 month':
        range = getThreeMonthRange();
        setShowDatePicker(false);
        getDateFilter(range.start, range.end);
        break;
      case 'Custom':
        setShowDatePicker(true);
        break;
      default:
        break;
    }
  };

const handleCustomDateRangeChange = (dates: any) => {
  if (dates?.length === 2) {
    const startDate = new Date(dates[0].valueOf());
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(dates[1].valueOf());
    endDate.setHours(0, 0, 0, 0);
    endDate.setDate(endDate.getDate() + 1); // Add 1 day to make it inclusive
    
    getDateFilter(startDate.getTime(), endDate.getTime());
  }
  setShowDatePicker(false);
};

  // Helper to parse JSON safely
  const safeParse = (str: string, fallback = {}) => {
    try { return JSON.parse(str); } catch { return fallback; }
  };

  const formatDurationToSeconds = (durationStr: any) => {
    // Example input: "0:00:01.190000"
    if (typeof durationStr === 'string' && durationStr.includes(':')) {
      const parts = durationStr.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const secParts = parts[2].split('.');
      const seconds = parseInt(secParts[0], 10);
      const milliseconds = parseInt(secParts[1], 10) || 0;
      // Total seconds (rounded to 2 decimal places)
      return (hours * 3600 + minutes * 60 + seconds + milliseconds / 1000000).toFixed(2) + ' sec';
    }
    // If already a number or string in seconds
    return durationStr + ' sec';
  };

  const { id: chatBotId } = useParams();
  const navigate = useNavigate();

  const getDateFilter = async (start: any, end: any) => {
    setIsLoading(true);
    const payload = {
      start_date: start,
      end_date: end,
    };
    try {
      await dispatchAutosolveTestHistory(chatBotId, payload);
    } catch (error) {
      console.error('Error fetching autosolve test history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (chatBotId) {
      dispatchBotDetails(chatBotId);
      const { start, end } = getMonthRange();
      getDateFilter(start, end);
    }
  }, [chatBotId]);

  function getDayRange() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextDay = new Date(today.getTime());
    nextDay.setDate(today.getDate() + 1);
    return {
      start: today.getTime(),
      end: nextDay.getTime()
    };
  }

  function getWeekRange() {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end.getTime());
    start.setDate(end.getDate() - 7);
    end.setDate(end.getDate() + 1);
    return {
      start: start.getTime(),
      end: end.getTime()
    };
  }

  function getMonthRange() {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end.getTime());
    start.setMonth(end.getMonth() - 1);
    end.setDate(end.getDate() + 1);
    return {
      start: start.getTime(),
      end: end.getTime()
    };
  }

  function getThreeMonthRange() {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end.getTime());
    start.setMonth(end.getMonth() - 3);
    end.setDate(end.getDate() + 1);
    return {
      start: start.getTime(),
      end: end.getTime()
    };
  }

  const handleDownloadClick = () => {
    if (autosolveTestHistory && autosolveTestHistory.length > 0) {
      const columns = [
        'Date',
        'User_ID',
        'Session_Input',
        'Conversation_ID',
        'Duration',
        'Start_Time',
        'Bot_Input',
        'Bot_Output',
        'Status'
      ];
      const defaultWidth = 200;
      const defaultHeight = 50;

      const dataToExport = autosolveTestHistory.map((item: any) => {
        const botInput = safeParse(item.bot_input, {});
        const botOutput = safeParse(item.bot_output, {});
        
        return {
          Date: item.run_time
            ? new Date(item.run_time).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : 'NA',
          User_ID: item.user_id || 'NA',
          Session_Input: botInput.user_query || 'NA',
          Conversation_ID: item.conversation_id || 'NA',
          Duration: formatDurationToSeconds(item.duration),
          Start_Time: item.run_time
            ? new Date(item.run_time).toLocaleTimeString('en-GB')
            : 'NA',
          Bot_Input: JSON.stringify(botInput) || 'NA',
          Bot_Output: JSON.stringify(botOutput) || 'NA',
          Status: item.status || 'NA'
        };
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport, { header: columns });
      columns.forEach((column, columnIndex) => {
        ws['!cols'] = ws['!cols'] || [];
        ws['!cols'][columnIndex] = { wpx: defaultWidth };
      });

      for (let rowIndex = 1; rowIndex <= dataToExport.length; rowIndex++) {
        ws[`!rows`] = ws[`!rows`] || [];
        ws[`!rows`][rowIndex] = { hpx: defaultHeight };
      }

      const formattedDate = new Date()
        .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
        .replace(/\//g, '');
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(wb, `${botDetails?.agent_config?.agent_name || 'AutosolveTestHistory'}_${formattedDate}.xlsx`);
    }
  };

  const mappedData = (autosolveTestHistory || []).map((item: any) => {
    const botInput = safeParse(item.bot_input, {});
    const formattedDuration = formatDurationToSeconds(item.duration);
    return {
      key: item.history_id,
      date: item.run_time
        ? new Date(item.run_time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '',
      user_id: item.user_id,
      session_input: botInput.user_query || '',
      conversation_id: item.conversation_id,
      duration: formattedDuration,
      start_time: item.run_time
        ? new Date(item.run_time).toLocaleTimeString('en-GB')
        : '',
      detailedLogs: (
        <WizrTag
          data={['Detailed Logs']}
          background="#EAECF1"
          textColor="#1D1F22"
          checkable={false}
          style={{
            borderRadius: '20px',
            fontWeight: 400,
            padding: '4px 18px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => {
            navigate(`/autosolvetesthistory/${chatBotId}/detailed-logs/${item.conversation_id}`);
          }}
        />
      ),
    };
  });

  return (
    <WizrFlexLayout>
      <WizrFlexLayout justifyContent='space-between' alignItems='center' flexDirection='row'>
        <WizrUserJourney
          userJourney={[
            {
              title: (
                <WizrButton
                  icon={<ArrowLeftOutlined style={{ fontSize: "22px", marginRight: "10px" }} />}
                  type='text'
                  text={<WizrText type='h6'>{botDetails?.agent_config?.agent_name}</WizrText>}
                  onClick={() => navigate(-1)}
                />
              ),
            },
            { title: 'Test' },
            { title: 'History' },
          ]}
        />
        <WizrTag
          data={uniqueTagList}
          onClick={getDateRangeFilter}
          background={'#EAECF1'}
          textColor='1D1F22'
          checkable={true}
          checkedValue={2}
        />
        {showDatePicker ? (
          <Popover content={
            <WizrFlexLayout textAlign='left' background='#FFF'>
              <DatePicker.RangePicker onChange={handleCustomDateRangeChange} />
            </WizrFlexLayout>
          } title='' trigger='click'>
            <DatePicker.RangePicker onChange={handleCustomDateRangeChange} />
          </Popover>
        ) : (
          <></>
        )}
        <StyledButtonWrapper>
          <WizrButton icon={<DownloadIcon />} text='' type='text' onClick={handleDownloadClick} />
        </StyledButtonWrapper>
      </WizrFlexLayout>
    <div ref={listInnerRef} style={{ height: '100vh', overflowY: 'auto', flex: '6' }}>
      {isLoading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%', 
          width: '100%' 
        }}>
          <WizrLoader 
            loading={isLoading}
            size="large"
          />
        </div>
      ) : (
        <>
          <WizrTable
            type="autosolvetesthistory"
            dataSource={mappedData}
            rowKey="key"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
          {loading && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              width: '100%', 
              height: '100%', 
              padding: '5px' 
            }}>
              <Spin />
            </div>
          )}
        </>
      )}
    </div>
    </WizrFlexLayout>
  );
};

export default AutoSolveTestHistory;
